import { DateTime } from "luxon";

export type DateTimeRange = { start: DateTime; end: DateTime };

export type CalendarDateAnchors = {
  currentDate: string;
  nextWeekStart: string;
  nextWeekEnd: string;
};

/**
 * Parses an ISO datetime using its explicit offset when present, or the supplied timezone for an
 * offset-less wall-clock value. This keeps EHR datetimes stable when the server runs in another zone.
 */
export function parseIsoDateTimeWithDefaultZone(value: string, timezone?: string | null): DateTime {
  return DateTime.fromISO(value, {
    setZone: true,
    zone: timezone?.trim() || undefined,
  });
}

/** Returns stable clinic-local ISO anchors for interpreting caller-relative calendar phrases. */
export function calendarDateAnchorsInTimezone(timezone?: string, now: Date = new Date()): CalendarDateAnchors {
  const requestedZone = timezone?.trim();
  const zonedNow = DateTime.fromJSDate(now, requestedZone ? { zone: requestedZone } : undefined);
  const current = zonedNow.isValid ? zonedNow : DateTime.fromJSDate(now);
  const currentDate = current.toISODate();
  const nextWeekStart = current.plus({ weeks: 1 }).startOf("week").toISODate();
  const nextWeekEnd = current.plus({ weeks: 1 }).endOf("week").toISODate();

  if (!currentDate || !nextWeekStart || !nextWeekEnd) {
    throw new Error("Could not derive calendar-date anchors");
  }

  return { currentDate, nextWeekStart, nextWeekEnd };
}

export function parseIsoDateTimeRange(rangeStart?: string, rangeEnd?: string): DateTimeRange | null {
  if (!rangeStart || !rangeEnd) {
    return null;
  }

  const start = DateTime.fromISO(rangeStart, { setZone: true });
  const end = DateTime.fromISO(rangeEnd, { setZone: true });

  if (!start.isValid || !end.isValid) {
    return null;
  }

  return { start, end };
}

export function dateTimeRangesOverlap(a: DateTimeRange, b: DateTimeRange): boolean {
  return a.start < b.end && b.start < a.end;
}

/**
 * Formats an ISO 8601 date-time string into a human-readable string.
 * Preserves the local time from the ISO string's timezone offset.
 * Example: '2025-12-15T09:30:00.000-05:00' -> "Monday, December 15 at 9:30 AM"
 */
export function formatDateTimeDisplay(isoString: string, options: { includeYear?: boolean } = {}): string {
  const dt = DateTime.fromISO(isoString, { setZone: true });
  const dateFormat = options.includeYear ? "EEEE, MMMM d, yyyy 'at' h:mm a" : "EEEE, MMMM d 'at' h:mm a";

  return dt.toFormat(dateFormat);
}

/**
 * Keeps same-year date-time speech concise while including the year across calendar-year boundaries.
 * The reference instant is compared in the date-time's local zone so the decision remains correct
 * around New Year's even when the server runs in a different timezone.
 */
export function formatDateTimeDisplayWithYearIfDifferent(
  isoString: string,
  referenceDate: DateTime = DateTime.now()
): string {
  const date = DateTime.fromISO(isoString, { setZone: true });
  const referenceYear = date.isValid && referenceDate.isValid ? referenceDate.setZone(date.zone).year : null;

  return formatDateTimeDisplay(isoString, { includeYear: referenceYear !== null && date.year !== referenceYear });
}

/**
 * Returns the calendar date carried by an ISO datetime without shifting it into the runtime timezone.
 * Invalid input is returned unchanged so existing display callers retain their fallback behavior.
 */
export function formatIsoDate(isoString: string): string {
  const date = DateTime.fromISO(isoString, { setZone: true });

  return date.isValid ? date.toISODate()! : isoString;
}

/**
 * Converts a date string from MM/DD/YYYY to YYYY-MM-DD format.
 * If the string is already in YYYY-MM-DD, it returns it as is.
 * Returns null for unrecognized formats.
 * @param dateStr - The date string to convert.
 * @returns The formatted date string or null.
 */
export function formatUsDateToYyyyMmDd(dateStr: string | null): string | null {
  if (!dateStr) return null;

  // Check if it's already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }

  // Check for MM/DD/YYYY format and convert
  const mmDdYyyyRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const match = dateStr.match(mmDdYyyyRegex);

  if (match) {
    const month = match[1].padStart(2, "0");
    const day = match[2].padStart(2, "0");
    const year = match[3];

    return `${year}-${month}-${day}`;
  }

  // Return null if the format is not recognized to avoid incorrect matches
  return null;
}

type FormatDateDisplayResult = { date: string; formatted: string } | { date: string; error: string };
export function formatDateDisplay(date: string): FormatDateDisplayResult {
  const dt = DateTime.fromISO(date);

  if (!dt.isValid) {
    return { date, error: `Invalid date "${date}". Use YYYY-MM-DD format.` };
  }

  return { date, formatted: dt.toFormat("EEEE, MMMM d, yyyy") };
}

export type LeapYearDateKind = "start" | "end";

/**
 * Adjusts February 29 dates to valid dates for systems that don't support leap day.
 * Start dates are moved to February 28, end dates are moved to March 1.
 * Date format: yyyy-mm-dd or mm/dd/yyyy
 */
export function adjustLeapYearDate(date: string, kind: LeapYearDateKind): string {
  if (date.includes("-")) {
    if (!/^\d{4}-02-29$/.test(date)) {
      return date;
    }

    const year = date.slice(0, 4);

    return kind === "start" ? `${year}-02-28` : `${year}-03-01`;
  }

  if (!date.startsWith("02/29/")) {
    return date;
  }

  return kind === "start" ? `02/28/${date.slice(6)}` : `03/01/${date.slice(6)}`;
}

export function getDurationMinutes(start: Date, end: Date): number {
  const durationMs = end.getTime() - start.getTime();

  return Math.max(Math.round(durationMs / 60000), 0);
}

export function isValidTimestamp(value: string): value is string {
  return !Number.isNaN(Date.parse(value));
}

// Formats a date-of-birth may arrive in. The collect tool asks the model for
// mm/dd/yyyy, but the model doesn't always zero-pad or keep the separator stable
// across turns ("8/9/1990" vs "08/09/1990"), and it occasionally hands back ISO.
// `M`/`d` accept 1–2 digits, so the slash form covers both padded and unpadded.
const DOB_INPUT_FORMATS = ["M/d/yyyy", "M-d-yyyy", "yyyy-MM-dd"];

/**
 * Canonicalizes a collected date of birth to ISO `yyyy-MM-dd`, or returns null if
 * it can't be parsed. Use it to compare two DOB captures for the same calendar
 * date without being fooled by formatting drift (padding/separators) between turns.
 */
export function normalizeDateOfBirth(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();

  for (const format of DOB_INPUT_FORMATS) {
    const dt = DateTime.fromFormat(trimmed, format);

    if (dt.isValid) {
      return dt.toFormat("yyyy-MM-dd");
    }
  }

  return null;
}

/** Nobody calling a clinic was born before this. Also the cutoff the Mindpath ingest hack picked. */
export const DOB_MIN_YEAR = 1900;

/**
 * Coarse structural ceiling for a birth year, for schemas that need a static bound.
 * The precise "not in the future" test is `isPlausibleDateOfBirth`, which needs the
 * current date and so cannot be a Zod `.max()`.
 */
export const DOB_MAX_YEAR = 2100;

/** `m/d/yy` or `m-d-yy` — a two-digit year, which needs a century before it means anything. */
const TWO_DIGIT_YEAR_DOB = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2})$/;

/** The longest verified human lifespan in whole years. A birth date further back is nobody alive. */
export const OLDEST_VERIFIED_AGE_YEARS = 122;

/**
 * True when an ISO date could actually be somebody's birthday: inside the last
 * `DOB_MIN_YEAR`-to-now window, and not in the future.
 *
 * Exists because nothing else in the pipeline range-checks a DOB — `normalizeDateOfBirth`
 * canonicalizes formatting, Postgres `date` accepts year 8864, and the Athena create posts
 * a raw form body. Three charts reached the EHR with years 8864, 3048 and 1782 before this.
 */
export function isPlausibleDateOfBirth(isoDate: string, now: DateTime = DateTime.now()): boolean {
  const dt = DateTime.fromISO(isoDate);

  if (!dt.isValid) return false;

  return dt.year >= DOB_MIN_YEAR && dt.startOf("day") <= now.startOf("day");
}

export type ResolvedDateOfBirth =
  | { ok: true; iso: string }
  | { ok: false; reason: "unparseable" | "implausible" | "ambiguous" };

/**
 * Parses a collected date of birth to a plausible ISO date, resolving a two-digit year
 * only when exactly one century is possible.
 *
 * Callers speak dates as a compact run ("eight eight sixty four"), which reaches the model as
 * `8/8/64`. Luxon's own `yy` cutoff is a fixed year (49), so it reads `4/30/48` as **2048** —
 * a future date. Rejecting the future candidate gets 1948. When both centuries are possible,
 * ask for all four digits: an age cutoff cannot distinguish a centenarian from a child.
 *
 * A caller can opt in to `maxTwoDigitYearAge`, which also drops a reading that would make the
 * patient older than that today. At `OLDEST_VERIFIED_AGE_YEARS`, `10/17/01` is 2001, since nobody
 * born in 1901 is alive, while `1/1/05` stays ambiguous.
 *
 * A run with the separators missing (`02/2445`, `71572`) is deliberately NOT re-segmented here:
 * the model has the caller's actual words and should do that, and every observed tool argument
 * already arrived well-formed. Guessing at digit boundaries in code would just relocate the bug.
 *
 * Deliberately separate from `normalizeDateOfBirth`, which stays a pure formatting
 * canonicalizer used for change detection and cache keys.
 */
export function resolveDateOfBirth(
  value: string | null | undefined,
  now: DateTime = DateTime.now(),
  { maxTwoDigitYearAge }: { readonly maxTwoDigitYearAge?: number } = {}
): ResolvedDateOfBirth {
  const trimmed = value?.trim();

  if (!trimmed) return { ok: false, reason: "unparseable" };

  const fourDigitYear = normalizeDateOfBirth(trimmed);

  if (fourDigitYear) {
    return isPlausibleDateOfBirth(fourDigitYear, now)
      ? { ok: true, iso: fourDigitYear }
      : { ok: false, reason: "implausible" };
  }

  const match = TWO_DIGIT_YEAR_DOB.exec(trimmed);

  if (!match) return { ok: false, reason: "unparseable" };

  const [, month, day, shortYear] = match;
  const suffix = Number(shortYear);

  const [nineteenHundreds, twoThousands] = [1900 + suffix, 2000 + suffix].map((year) =>
    DateTime.fromObject({ year, month: Number(month), day: Number(day) })
  );
  const calendarCandidates = [nineteenHundreds, twoThousands].filter((date) => date.isValid);

  if (calendarCandidates.length === 0) return { ok: false, reason: "unparseable" };

  // Anyone born on or before this date is older than the caller's limit today.
  const tooOldOnOrBefore =
    maxTwoDigitYearAge === undefined ? null : now.minus({ years: maxTwoDigitYearAge + 1 }).toFormat("yyyy-MM-dd");
  const plausibleCandidates = calendarCandidates
    .map((date) => date.toFormat("yyyy-MM-dd"))
    .filter((iso) => isPlausibleDateOfBirth(iso, now) && (tooOldOnOrBefore === null || iso > tooOldOnOrBefore));

  if (plausibleCandidates.length > 1) return { ok: false, reason: "ambiguous" };

  const iso = plausibleCandidates[0];

  return iso ? { ok: true, iso } : { ok: false, reason: "implausible" };
}

const ORDINAL_DAY_WORDS = [
  "first",
  "second",
  "third",
  "fourth",
  "fifth",
  "sixth",
  "seventh",
  "eighth",
  "ninth",
  "tenth",
  "eleventh",
  "twelfth",
  "thirteenth",
  "fourteenth",
  "fifteenth",
  "sixteenth",
  "seventeenth",
  "eighteenth",
  "nineteenth",
  "twentieth",
  "twenty-first",
  "twenty-second",
  "twenty-third",
  "twenty-fourth",
  "twenty-fifth",
  "twenty-sixth",
  "twenty-seventh",
  "twenty-eighth",
  "twenty-ninth",
  "thirtieth",
  "thirty-first",
];

const CARDINAL_ONES = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
];

const CARDINAL_TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

function twoDigitWords(value: number): string {
  if (value < 20) return CARDINAL_ONES[value];

  const ones = value % 10;

  return ones === 0
    ? CARDINAL_TENS[Math.floor(value / 10)]
    : `${CARDINAL_TENS[Math.floor(value / 10)]}-${CARDINAL_ONES[ones]}`;
}

/** "1964" -> "nineteen sixty-four"; "2007" -> "two thousand seven". Covers 1900-2099. */
function yearWords(year: number): string {
  const century = Math.floor(year / 100);
  const remainder = year % 100;

  // 2000-2009 is idiomatically "two thousand N", not "twenty oh N".
  if (century === 20 && remainder < 10) {
    return remainder === 0 ? "two thousand" : `two thousand ${CARDINAL_ONES[remainder]}`;
  }

  const centuryPart = twoDigitWords(century);

  if (remainder === 0) return `${centuryPart} hundred`;
  if (remainder < 10) return `${centuryPart} oh ${CARDINAL_ONES[remainder]}`;

  return `${centuryPart} ${twoDigitWords(remainder)}`;
}

/**
 * Renders an ISO date of birth as the sentence fragment the agent should speak —
 * "August eighth, nineteen sixty-four".
 *
 * The read-back is the only checkpoint where a caller can catch a mis-captured date, and it
 * only works if the words describe the value we actually stored. Left to the model, it echoes
 * its own bad parse back in the caller's own phrasing ("eighty-eight sixty-four"), which reads
 * as agreement and gets confirmed. Rendering from the normalized date removes that failure mode.
 */
export function formatDateOfBirthForSpeech(isoDate: string): string | null {
  const dt = DateTime.fromISO(isoDate);

  // 2099 is `yearWords`' vocabulary ceiling, not a plausibility bound — see DOB_MAX_YEAR.
  if (!dt.isValid || dt.year < DOB_MIN_YEAR || dt.year > 2099) return null;

  return `${dt.toFormat("LLLL")} ${ORDINAL_DAY_WORDS[dt.day - 1]}, ${yearWords(dt.year)}`;
}
